import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Image, FileText, Download, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FileGallery({ requestId }) {
  const { lang } = useLanguage();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [blobUrls, setBlobUrls] = useState({});

  useEffect(() => {
    if (!requestId) return;
    const fetchFiles = async () => {
      try {
        const { data } = await axios.get(`${API}/requests/${requestId}/files`);
        setFiles(data.files || []);
        // Load previews
        const urls = {};
        for (const f of (data.files || [])) {
          if (f.content_type?.startsWith('image/')) {
            try {
              const resp = await axios.get(`${API}/files/${f.id}/download`, { responseType: 'blob' });
              urls[f.id] = URL.createObjectURL(resp.data);
            } catch { /* skip */ }
          }
        }
        setBlobUrls(urls);
      } catch { /* no files */ }
      setLoading(false);
    };
    fetchFiles();
    return () => Object.values(blobUrls).forEach(URL.revokeObjectURL);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const downloadFile = async (file) => {
    try {
      const resp = await axios.get(`${API}/files/${file.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_filename || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      console.error('Download failed');
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-[10px] text-[#6B8AB5]"><Loader2 size={12} className="animate-spin" /> Loading files...</div>;
  if (files.length === 0) return null;

  const text = lang === 'it' ? {
    attachments: 'Allegati',
    download: 'Scarica',
    prescription: 'Prescrizione medica',
    photo: 'Foto',
  } : {
    attachments: 'Attachments',
    download: 'Download',
    prescription: 'Medical prescription',
    photo: 'Photo',
  };

  return (
    <div className="border-t border-[#B8D4E8] pt-3 mt-3" data-testid="file-gallery">
      <div className="flex items-center gap-2 mb-2">
        <Image size={12} className="text-[#0A84FF]" />
        <span className="text-[10px] uppercase tracking-widest text-[#6B8AB5]">{text.attachments} ({files.length})</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {files.map((file) => (
          <div key={file.id} className="relative border border-[#B8D4E8] bg-[#E8F4FD] group overflow-hidden" data-testid={`gallery-file-${file.id}`}>
            {blobUrls[file.id] ? (
              <img src={blobUrls[file.id]} alt={file.original_filename} className="w-full h-20 object-cover" />
            ) : (
              <div className="w-full h-20 flex items-center justify-center">
                <FileText size={24} className="text-[#6B8AB5]" />
              </div>
            )}
            <button
              onClick={() => downloadFile(file)}
              className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center"
              data-testid={`download-gallery-${file.id}`}
            >
              <Download size={18} className="text-[#0D1B2A]" />
            </button>
            <div className="px-1.5 py-1 bg-[#E8F4FD]">
              <p className="text-[8px] text-[#6B8AB5] truncate">{file.original_filename}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
